import React from 'react';
import { ClipboardList, Clock, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Pending Approvals Widget - Shows approval summary by type
 */
const PendingApprovalsWidget = ({ approvals, totalPending, loading, onViewAll, onApproveAll }) => {
  const navigate = useNavigate();

  const handleViewAllClick = () => {
    if (onViewAll) {
      onViewAll();
    } else {
      navigate('/home/approvaltimeentries');
    }
  };

  const handleApprovalCardClick = () => {
    navigate('/home/approvaltimeentries');
  };
  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'timesheet':
        return '📋';
      case 'check-in':
      case 'checkin':
        return '✅';
      case 'check-out':
      case 'checkout':
        return '🚪';
      case 'correction':
        return '✏️';
      case 'overtime':
        return '⏰';
      default:
        return '📝';
    }
  };

  const getTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'check-in':
      case 'checkin':
        return 'Check-ins';
      case 'check-out':
      case 'checkout':
        return 'Check-outs';
      default:
        return type?.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Other';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-purple-600" />
          <h3 className="text-base font-semibold text-gray-800">Pending Approvals</h3>
          {totalPending > 0 && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
              {totalPending}
            </span>
          )}
        </div>
        <button
          onClick={handleViewAllClick}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All <ChevronRight size={14} />
        </button>
      </div>

      {/* Approval Type Cards */}
      {approvals?.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            {approvals.map((item, index) => (
              <div
                key={index}
                onClick={handleApprovalCardClick}
                className="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:border-purple-200 hover:bg-purple-50 transition-colors cursor-pointer hover:shadow-sm"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm">{getTypeIcon(item.activity_type)}</span>
                  <span className="text-xs font-medium text-gray-600 truncate">
                    {getTypeLabel(item.activity_type)}
                  </span>
                </div>
                <p className="text-xl font-bold text-gray-800">{item.pending_count}</p>
                <p className="text-xs text-gray-1000">
                  {item.oldest_pending_days > 0
                    ? `${item.oldest_pending_days}d ago`
                    : `${item.newest_pending_hours}h ago`}
                </p>
              </div>
            ))}
          </div>

          {/* Alert for old pending items */}
          {approvals.some((a) => a.oldest_pending_days > 2) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-center gap-2">
              <Clock size={14} className="text-amber-600" />
              <span className="text-xs text-amber-800">
                Some approvals pending &gt;2 days
              </span>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={handleApprovalCardClick}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
            >
              <CheckCircle size={14} />
              Review
            </button>
          </div>
        </>
      ) : (
        <div className="text-center py-4">
          <CheckCircle size={24} className="text-green-1000 mx-auto mb-2" />
          <p className="text-gray-600 text-sm font-medium">All caught up!</p>
          <p className="text-xs text-gray-400">No pending approvals</p>
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsWidget;
